// Theme Manager - Handles light/dark mode
export class ThemeManager {
  private currentTheme = "light";
  private listeners: ((theme: string) => void)[] = [];

  constructor() {
    this.currentTheme = this.getStoredTheme() || this.getSystemTheme();
  }

  async init(): Promise<void> {
    this.applyTheme();
    this.setupSystemThemeListener();
  }

  // Get stored theme from localStorage
  private getStoredTheme(): string | null {
    return localStorage.getItem("theme");
  }

  // Get system theme preference
  private getSystemTheme(): string {
    if (
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    ) {
      return "dark";
    }
    return "light";
  }

  // Set theme
  setTheme(theme: string): void {
    if (this.currentTheme !== theme) {
      this.currentTheme = theme;
      localStorage.setItem("theme", theme);
      this.applyTheme();
      this.notifyListeners();
    }
  }

  // Toggle theme
  toggleTheme(): void {
    const newTheme = this.currentTheme === "light" ? "dark" : "light";
    this.setTheme(newTheme);
  }

  // Get current theme
  getTheme(): string {
    return this.currentTheme;
  }

  // Apply theme to document
  private applyTheme(): void {
    document.documentElement.setAttribute("data-theme", this.currentTheme);

    // Update theme toggle button
    this.updateThemeToggle();
  }

  // Update theme toggle button
  private updateThemeToggle(): void {
    const themeToggle = document.getElementById("theme-toggle");
    if (!themeToggle) return;

    const icon = themeToggle.querySelector(".theme-icon");
    if (!icon) return;

    // Update icon based on current theme
    if (this.currentTheme === "dark") {
      // Sun icon for dark theme (to switch to light)
      icon.innerHTML = `
        <circle cx="12" cy="12" r="5"/>
        <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
      `;
    } else {
      // Moon icon for light theme (to switch to dark)
      icon.innerHTML = `
        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
      `;
    }
  }

  // Setup system theme change listener
  private setupSystemThemeListener(): void {
    if (window.matchMedia) {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

      mediaQuery.addEventListener("change", (e) => {
        // Only update if no theme is stored (user hasn't made a choice)
        if (!this.getStoredTheme()) {
          const systemTheme = e.matches ? "dark" : "light";
          this.setTheme(systemTheme);
        }
      });
    }
  }

  // Add theme change listener
  onThemeChange(listener: (theme: string) => void): void {
    this.listeners.push(listener);
  }

  // Remove theme change listener
  removeThemeListener(listener: (theme: string) => void): void {
    const index = this.listeners.indexOf(listener);
    if (index > -1) {
      this.listeners.splice(index, 1);
    }
  }

  // Notify listeners
  private notifyListeners(): void {
    this.listeners.forEach((listener) => {
      try {
        listener(this.currentTheme);
      } catch (error) {
        console.error("Error in theme listener:", error);
      }
    });
  }

  // Check if theme is dark
  isDark(): boolean {
    return this.currentTheme === "dark";
  }

  // Check if theme is light
  isLight(): boolean {
    return this.currentTheme === "light";
  }

  // Get theme color for meta tag
  getThemeColor(): string {
    return this.currentTheme === "dark" ? "#0f172a" : "#ffffff";
  }

  // Update theme color meta tag
  updateThemeColorMeta(): void {
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute("content", this.getThemeColor());
    }
  }

  // Apply theme to specific element
  applyThemeToElement(element: HTMLElement): void {
    element.setAttribute("data-theme", this.currentTheme);
  }

  // Remove theme from element
  removeThemeFromElement(element: HTMLElement): void {
    element.removeAttribute("data-theme");
  }

  // Get CSS custom property value
  getCSSVariable(variable: string): string {
    return getComputedStyle(document.documentElement)
      .getPropertyValue(variable)
      .trim();
  }

  // Set CSS custom property value
  setCSSVariable(variable: string, value: string): void {
    document.documentElement.style.setProperty(variable, value);
  }

  // Reset to system theme
  resetToSystemTheme(): void {
    localStorage.removeItem("theme");
    const systemTheme = this.getSystemTheme();
    this.setTheme(systemTheme);
  }

  // Get available themes
  getAvailableThemes(): string[] {
    return ["light", "dark"];
  }

  // Check if theme is supported
  isThemeSupported(theme: string): boolean {
    return this.getAvailableThemes().includes(theme);
  }
}
